// QuantityInput custom element for +/- buttons in cart
if (!customElements.get("quantity-input")) {
  customElements.define(
    "quantity-input",
    class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector("input");
        this.changeEvent = new Event("change", { bubbles: true });
        // Guard against null input (can happen during innerHTML replacement)
        if (this.input) {
          this.input.addEventListener("change", this.onInputChange.bind(this));
        }
        this.querySelectorAll("button").forEach((button) =>
          button.addEventListener("click", this.onButtonClick.bind(this)),
        );
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.quantityUpdate,
          this.validateQtyRules.bind(this),
        );
      }

      disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      onInputChange(event) {
        this.validateQtyRules();
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        const button = event.currentTarget;

        if (button.name === "plus") {
          this.input.stepUp();
        } else {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(this.changeEvent);
        }
      }

      validateQtyRules() {
        if (!this.input) return;
        const value = parseInt(this.input.value);
        if (this.input.min) {
          const buttonMinus = this.querySelector(
            '.quantity__button[name="minus"]',
          );
          if (buttonMinus) {
            buttonMinus.classList.toggle(
              "disabled",
              value <= parseInt(this.input.min),
            );
          }
        }
        if (this.input.max) {
          const buttonPlus = this.querySelector(
            '.quantity__button[name="plus"]',
          );
          if (buttonPlus) {
            buttonPlus.classList.toggle(
              "disabled",
              value >= parseInt(this.input.max),
            );
          }
        }
      }
    },
  );
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", (event) => this.onChange(event));
  }

  onChange(event) {
    const input = event.target.closest?.("[data-index]");
    if (!input) return;

    const line = Number(input.dataset.index);
    const lineItem =
      input.closest("[data-key]") ||
      document.getElementById(`CartDrawer-Item-${line}`) ||
      document.getElementById(`CartItem-${line}`);
    const lineKey = lineItem?.dataset?.key || "";
    const variantId = input.dataset.quantityVariantId || input.dataset.variantId || "";

    this.updateQuantity(
      line,
      Number(input.value),
      event,
      document.activeElement?.getAttribute?.("name") || "",
      variantId,
      lineKey,
    );
  }

  updateQuantity(line, quantity, event, name, variantId, lineKey = "") {
    const body = {
      quantity,
      sections: ["main-cart-items", "cart-icon-bubble", "cart-live-region-text"],
      sections_url: window.location.pathname,
    };
    if (lineKey) body.id = lineKey;
    else body.line = line;

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        return response.json();
      })
      .then(() => window.location.reload())
      .catch((error) => console.error("Cart update error:", error));
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}

if (!customElements.get("cart-remove-button")) {
  customElements.define(
    "cart-remove-button",
    class CartRemoveButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          const cartItems = this.closest("cart-items, cart-drawer-items");
          if (!cartItems?.updateQuantity) return;

          const line = Number(this.dataset.index);
          const lineItem =
            this.closest("[data-key]") ||
            document.getElementById(`CartDrawer-Item-${line}`) ||
            document.getElementById(`CartItem-${line}`);
          const lineKey =
            this.dataset.key ||
            event.target.closest?.("[data-key]")?.dataset?.key ||
            lineItem?.dataset?.key ||
            "";
          const variantId =
            event.target.closest?.("[data-variant-id]")?.dataset?.variantId || "";

          cartItems.updateQuantity(line, 0, event, "remove", variantId, lineKey);
        });
      }
    },
  );
}

if (!window.__onebedCartRemoveCaptureInstalled) {
  window.__onebedCartRemoveCaptureInstalled = true;
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const removeWrapper = target?.closest(".crt_remove_wrp");
      const removeControl =
        target?.closest("cart-remove-button, .cart-drawer-item__remove-btn") ||
        removeWrapper?.querySelector("cart-remove-button");
      if (!removeControl) return;

      const cartItems = removeControl.closest("cart-drawer-items");
      if (!cartItems?.updateQuantity) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const component =
        removeControl.matches("cart-remove-button")
          ? removeControl
          : removeControl.closest("cart-remove-button");
      const line = Number(component?.dataset.index);
      const lineItem =
        removeControl.closest("[data-key]") ||
        document.getElementById(`CartDrawer-Item-${line}`);
      const lineKey =
        component?.dataset.key ||
        removeControl.dataset.key ||
        lineItem?.dataset?.key ||
        "";
      const variantId =
        removeControl.closest("[data-variant-id]")?.dataset?.variantId || "";

      if (!line && !lineKey) return;
      cartItems.updateQuantity(line, 0, event, "remove", variantId, lineKey);
    },
    true,
  );
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this),
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    // If server cart has lines but the drawer DOM is empty/stale, refresh once.
    this.syncFromServerIfNeeded();

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
    document.documentElement.classList.add("overflow-hidden");
  }

  syncFromServerIfNeeded() {
    if (this.dataset.bmsSyncing === "true") return;
    const painted = this.querySelector('[id^="CartDrawer-Item-"]');
    if (painted) {
      this.hideEmptyState();
      return;
    }

    this.dataset.bmsSyncing = "true";
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        if (!cart?.item_count) {
          this.showEmptyState();
          return null;
        }
        this.hideEmptyState();
        return fetch(`${routes.cart_url}?section_id=cart-drawer`).then((response) =>
          response.text(),
        );
      })
      .then((sectionHtml) => {
        if (!sectionHtml) return;
        const cartDrawerRoot = document.getElementById("CartDrawer");
        const nextInner = this.getSectionInnerHTML(sectionHtml, "#CartDrawer");
        if (cartDrawerRoot && nextInner != null) {
          cartDrawerRoot.innerHTML = nextInner;
          this.hideEmptyState();
          this.querySelector("#CartDrawer-Overlay")?.addEventListener(
            "click",
            this.close.bind(this),
          );
        }
      })
      .catch((error) => console.error("Cart drawer sync failed:", error))
      .finally(() => {
        delete this.dataset.bmsSyncing;
      });
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
    document.documentElement.classList.remove("overflow-hidden");
    // Also remove overflow hidden from html element to ensure scrolling works
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  hideEmptyState() {
    this.classList.remove("is-empty");
    this.querySelector("cart-drawer-items")?.classList.remove("is-empty");
    const empty = this.querySelector(".drawer__inner-empty");
    if (empty) {
      empty.hidden = true;
      empty.style.display = "none";
      empty.style.visibility = "hidden";
      empty.style.pointerEvents = "none";
      empty.style.opacity = "0";
    }
  }

  showEmptyState() {
    this.classList.add("is-empty");
    this.querySelector("cart-drawer-items")?.classList.add("is-empty");
    const empty = this.querySelector(".drawer__inner-empty");
    if (empty) {
      empty.hidden = false;
      empty.style.display = "";
      empty.style.visibility = "";
      empty.style.pointerEvents = "";
      empty.style.opacity = "";
    }
  }

  renderContents(parsedState) {
    // `is-empty` belongs to <cart-drawer>, not .drawer__inner. Clear both
    // drawer flags before painting the sections returned by /cart/add.js.
    this.hideEmptyState();
    this.productId = parsedState.id;

    const sections = parsedState?.sections || {};
    const paint = () => {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;

        const html = sections[section.id];
        if (!html) return;
        const nextHtml = this.getSectionInnerHTML(html, section.selector);
        if (nextHtml == null) return;
        sectionElement.innerHTML = nextHtml;
      });
    };

    paint();

    const painted = this.querySelector('[id^="CartDrawer-Item-"]');
    const paintAndOpen = () => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener(
        "click",
        this.close.bind(this),
      );
      this.open();
    };

    // Gift interceptors can drop section HTML; recover from the live cart section.
    if (!painted) {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((sectionHtml) => {
          const cartDrawerRoot = document.getElementById("CartDrawer");
          const nextInner = this.getSectionInnerHTML(sectionHtml, "#CartDrawer");
          if (cartDrawerRoot && nextInner != null) {
            cartDrawerRoot.innerHTML = nextInner;
          }
          if (this.querySelector('[id^="CartDrawer-Item-"]')) {
            this.hideEmptyState();
          } else {
            this.showEmptyState();
          }
        })
        .catch((error) => console.error("Cart drawer fallback paint failed:", error))
        .finally(paintAndOpen);
      return;
    }

    this.hideEmptyState();
    setTimeout(paintAndOpen);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return null;
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector || ".shopify-section");
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: "#CartDrawer-CartItems",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (
          event.source === "cart-items" ||
          event.source === "cart-size-selector"
        ) {
          return;
        }
        // gift-display refreshes after orphan-gift cleanup on parent delete.
        return this.onCartUpdate();
      },
    );

    this.boundGiftRefresh = () => this.onCartUpdate();
    document.addEventListener("cart:refresh", this.boundGiftRefresh);
    window.addEventListener("cart:refresh", this.boundGiftRefresh);

    const form = this.querySelector("#CartDrawer-Form") || this.querySelector("form");
    if (form && form.dataset.bmsAjaxGuard !== "true") {
      form.dataset.bmsAjaxGuard = "true";
      form.addEventListener(
        "submit",
        (event) => {
          const submitter = event.submitter;
          // Allow checkout only; block accidental full-page cart POSTs from remove/qty.
          if (submitter?.getAttribute?.("name") === "checkout") return;
          event.preventDefault();
          event.stopPropagation();
        },
        true,
      );
    }
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    if (this.boundGiftRefresh) {
      document.removeEventListener("cart:refresh", this.boundGiftRefresh);
      window.removeEventListener("cart:refresh", this.boundGiftRefresh);
    }
  }

  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = this.getSectionInnerHTML(responseText, "#CartDrawer-CartItems");
        const target = document.getElementById("CartDrawer-CartItems");
        if (target && html != null) target.innerHTML = html;

        const headingSource = this.getSectionInnerHTML(responseText, ".drawer__heading");
        const headingTarget = document.querySelector("cart-drawer .drawer__heading");
        if (headingTarget && headingSource) headingTarget.textContent = headingSource;

        const footerHtml = this.getSectionInnerHTML(responseText, ".cart-drawer__footer");
        const footerTarget = document.querySelector(".cart-drawer__footer");
        if (footerTarget && footerHtml) footerTarget.innerHTML = footerHtml;

        const drawer = document.querySelector("cart-drawer");
        const hasItems = Boolean(drawer?.querySelector('[id^="CartDrawer-Item-"]'));
        if (!drawer) return;
        if (hasItems && typeof drawer.hideEmptyState === "function") {
          drawer.hideEmptyState();
        } else if (!hasItems && typeof drawer.showEmptyState === "function") {
          drawer.showEmptyState();
        } else {
          drawer.classList.toggle("is-empty", !hasItems);
        }
      })
      .catch((error) => console.error("Cart drawer refresh failed:", error));
  }

  // Override to update cart drawer footer separately
  updateQuantity(line, quantity, event, name, variantId, lineKey = "") {
    this.enableLoading(line, { removing: quantity === 0 });

    const lineEl =
      document.getElementById(`CartDrawer-Item-${line}`) ||
      document.getElementById(`CartItem-${line}`);
    const resolvedKey = lineKey || lineEl?.dataset?.key;
    const body = {
      quantity,
      sections: ["cart-drawer", "cart-icon-bubble"],
      sections_url: window.location.pathname,
    };

    // Keep nested free-gift rows in lockstep with the parent quantity (or clear
    // them on parent delete) in one /cart/update.js call — avoids the slow
    // gift-display reconcile round-trips and Free-price flicker.
    const giftNodes = Array.from(
      lineEl
        ?.closest(".bms-cart-line-group")
        ?.querySelectorAll('[data-bms-gift="true"][data-key]') || [],
    );
    const parentQtyCurrent = Math.max(1, Number(lineEl?.dataset?.quantity) || 1);

    let requestUrl = routes.cart_change_url;
    let requestBody = body;
    if (resolvedKey && giftNodes.length) {
      const updates = { [resolvedKey]: quantity };
      const seenVariants = new Set();
      giftNodes.forEach((node) => {
        const key = node.dataset.key;
        if (!key) return;
        if (quantity === 0) {
          updates[key] = 0;
          return;
        }
        const variantId = String(
          node.dataset.variationId || node.dataset.variantId || key,
        );
        if (seenVariants.has(variantId)) {
          updates[key] = 0;
          return;
        }
        seenVariants.add(variantId);
        const currentGiftQty = Math.max(1, Number(node.dataset.quantity) || 1);
        const unit = Math.max(1, Math.round(currentGiftQty / parentQtyCurrent));
        updates[key] = quantity * unit;
      });
      requestUrl = routes.cart_update_url;
      requestBody = {
        updates,
        sections: body.sections,
        sections_url: body.sections_url,
      };
    } else if (resolvedKey) {
      // Prefer line-item key so deletes stay correct when gift rows change indexes.
      body.id = resolvedKey;
    } else {
      body.line = line;
    }

    fetch(requestUrl, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(requestBody) },
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(
              `HTTP error! status: ${response.status}, message: ${text}`,
            );
          });
        }
        return response.text();
      })
      .then((state) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (e) {
          throw new Error(
            "Failed to parse cart JSON response: " + state.substring(0, 100),
          );
        }

        if (parsedState.errors) {
          const quantityElement = document.getElementById(
            `Drawer-quantity-${line}`,
          );
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute("value");
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Update cart empty state
        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        if (cartDrawerWrapper) {
          if (parsedState.item_count === 0) {
            if (typeof cartDrawerWrapper.showEmptyState === "function") {
              cartDrawerWrapper.showEmptyState();
            } else {
              cartDrawerWrapper.classList.add("is-empty");
            }
          } else if (typeof cartDrawerWrapper.hideEmptyState === "function") {
            cartDrawerWrapper.hideEmptyState();
          } else {
            cartDrawerWrapper.classList.remove("is-empty");
          }
        }

        const sectionHtml = parsedState.sections?.["cart-drawer"];
        const applyDrawerFromSection = (html) => {
          if (!html) return false;
          const cartItemsHtml = this.getSectionInnerHTML(
            html,
            "#CartDrawer-CartItems",
          );
          const cartItemsTarget = document.getElementById(
            "CartDrawer-CartItems",
          );
          if (cartItemsTarget && cartItemsHtml !== null) {
            cartItemsTarget.innerHTML = cartItemsHtml;
          }

          const headingSource = this.getSectionInnerHTML(html, ".drawer__heading");
          const headingTarget = document.querySelector(
            "cart-drawer .drawer__heading",
          );
          if (headingTarget && headingSource) {
            headingTarget.textContent = headingSource;
          }

          const footerHtml = this.getSectionInnerHTML(
            html,
            ".cart-drawer__footer",
          );
          const footerTarget = document.querySelector(".cart-drawer__footer");
          if (footerTarget && footerHtml) {
            footerTarget.innerHTML = footerHtml;
          }
          return true;
        };

        const finishPaint = () => {
          // Update cart icon bubble
          const cartIconHtml = this.getSectionInnerHTML(
            parsedState.sections?.["cart-icon-bubble"],
            ".shopify-section",
          );
          const cartIconTarget = document.getElementById("cart-icon-bubble");
          if (cartIconTarget && cartIconHtml) {
            cartIconTarget.innerHTML = cartIconHtml;
          }

          // Update live regions
          const updatedValue = parsedState.items?.[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;
          let message = "";
          if (typeof updatedValue === "undefined" && quantity !== 0) {
            message = window.cartStrings?.error || "An error occurred";
          }
          this.updateLiveRegions(line, message);

          // Publish cart update event
          if (
            typeof publish !== "undefined" &&
            typeof PUB_SUB_EVENTS !== "undefined"
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "cart-items",
              cartData: parsedState,
              variantId: variantId,
            });
          }
        };

        // Empty cart: clear line items. Empty-state markup stays in the DOM
        // and is shown via cart-drawer.is-empty CSS.
        if (parsedState.item_count === 0) {
          const cartItemsTarget = document.getElementById(
            "CartDrawer-CartItems",
          );
          if (cartItemsTarget) {
            cartItemsTarget.innerHTML = "";
          }

          const heading = cartDrawerWrapper?.querySelector(".drawer__heading");
          if (heading) {
            heading.textContent = "Your Cart (0)";
          }
          applyDrawerFromSection(sectionHtml);
          finishPaint();
          return;
        }

        if (applyDrawerFromSection(sectionHtml)) {
          finishPaint();
          return;
        }

        // Fallback when sections are missing from intercepted responses.
        return fetch(`${routes.cart_url}?section_id=cart-drawer`)
          .then((response) => response.text())
          .then((responseText) => {
            applyDrawerFromSection(responseText);
            finishPaint();
          });
      })
      .catch((e) => {
        console.error("Cart update error details:", e);
        const errors = document.getElementById("CartDrawer-CartErrors");
        if (errors) {
          if (e.message.includes("429")) {
            errors.textContent =
              "Too many requests. Please wait a moment and try again.";
          } else {
            errors.textContent =
              window.cartStrings?.error ||
              "An error occurred while updating your cart.";
          }
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(
      `CartDrawer-LineItemError-${line}`,
    );
    if (lineItemError) {
      const errorText = lineItemError.querySelector(".cart-item__error-text");
      if (errorText) {
        errorText.textContent = message;
      }
    }

    const cartStatus = document.getElementById("CartDrawer-LiveRegionText");
    if (cartStatus) {
      cartStatus.setAttribute("aria-hidden", false);
      setTimeout(() => {
        cartStatus.setAttribute("aria-hidden", true);
      }, 1000);
    }
  }

  enableLoading(line, { removing = false } = {}) {
    const mainCartItems = document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.add("cart__items--disabled");
    }

    const cartItem = document.getElementById(`CartDrawer-Item-${line}`);
    if (!cartItem) return;

    const spinner = cartItem.querySelector(".loading__spinner");
    if (spinner) spinner.classList.remove("hidden");

    const group = cartItem.closest(".bms-cart-line-group") || cartItem;
    group.classList.add("is-updating");
    if (removing) {
      group.classList.add("is-removing");
      const overlay = group.querySelector(".bms-cart-line-group__removing");
      if (overlay) overlay.hidden = false;
      group
        .querySelectorAll(".cart-drawer-item__remove-btn, cart-remove-button button")
        .forEach((button) => {
          button.disabled = true;
          button.setAttribute("aria-busy", "true");
        });
    }
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.remove("cart__items--disabled");
    }

    const cartItem = document.getElementById(`CartDrawer-Item-${line}`);
    if (!cartItem) return;

    const spinner = cartItem.querySelector(".loading__spinner");
    if (spinner) spinner.classList.add("hidden");

    const group = cartItem.closest(".bms-cart-line-group") || cartItem;
    group.classList.remove("is-updating", "is-removing");
    const overlay = group.querySelector(".bms-cart-line-group__removing");
    if (overlay) overlay.hidden = true;
    group
      .querySelectorAll(".cart-drawer-item__remove-btn, cart-remove-button button")
      .forEach((button) => {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      });
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}

// Cart Size Selector - handles size changes in cart drawer
class CartSizeSelector {
  constructor() {
    this.init();
  }

  init() {
    // Use event delegation for size dropdowns
    document.addEventListener(
      "change",
      (event) => {
        if (event.target.classList.contains("cart-item__size-dropdown")) {
          // Stop propagation to prevent CartItems from handling this as a quantity change
          event.stopPropagation();
          this.handleSizeChange(event.target);
        }
      },
      true,
    ); // Use capture phase to intercept before CartItems
  }

  getConfig() {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }

  showLoadingForItem(lineIndex) {
    const cartItem = document.getElementById(`CartDrawer-Item-${lineIndex}`);
    if (cartItem) {
      const spinner = cartItem.querySelector(".loading__spinner");
      if (spinner) {
        spinner.classList.remove("hidden");
      }
    }
    // Also disable the cart items container
    const cartDrawerItems = document.querySelector("cart-drawer-items");
    if (cartDrawerItems) {
      cartDrawerItems.classList.add("cart__items--disabled");
    }
  }

  hideLoadingForItem(lineIndex) {
    const cartItem = document.getElementById(`CartDrawer-Item-${lineIndex}`);
    if (cartItem) {
      const spinner = cartItem.querySelector(".loading__spinner");
      if (spinner) {
        spinner.classList.add("hidden");
      }
    }
    const cartDrawerItems = document.querySelector("cart-drawer-items");
    if (cartDrawerItems) {
      cartDrawerItems.classList.remove("cart__items--disabled");
    }
  }

  async handleSizeChange(select) {
    const lineIndex = select.dataset.lineIndex;
    const newVariantId = select.value;
    const currentVariantId = select.dataset.currentVariantId;

    if (newVariantId === currentVariantId) return;

    // Get the current quantity for this line item - use the new layout structure
    const cartItem = select.closest(".cart-drawer-item");
    const quantityInput = cartItem
      ? cartItem.querySelector(".quantity__input")
      : null;
    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

    // Show loading state with spinner
    this.showLoadingForItem(lineIndex);

    const config = this.getConfig();

    try {
      // First, remove the old item
      const removeResponse = await fetch(`${routes.cart_change_url}`, {
        ...config,
        body: JSON.stringify({
          line: parseInt(lineIndex),
          quantity: 0,
        }),
      });

      if (!removeResponse.ok) {
        const errorText = await removeResponse.text();
        throw new Error(
          `Remove failed! status: ${removeResponse.status}, message: ${errorText}`,
        );
      }

      // Then add the new variant with the same quantity and request sections directly
      const addResponse = await fetch(`${routes.cart_add_url}`, {
        ...config,
        body: JSON.stringify({
          id: parseInt(newVariantId),
          quantity: quantity,
          sections: ["cart-drawer", "cart-icon-bubble"],
          sections_url: window.location.pathname,
        }),
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        throw new Error(
          `Add failed! status: ${addResponse.status}, message: ${errorText}`,
        );
      }

      const addResult = await addResponse.json();

      if (addResult.status) {
        // Error occurred
        console.error("Error adding new variant:", addResult.description);
        this.hideLoadingForItem(lineIndex);
        return;
      }

      // Update UI directly from addResult
      if (addResult.sections) {
        // Update cart items content
        const cartItemsHtml = this.getSectionInnerHTML(
          addResult.sections["cart-drawer"],
          "#CartDrawer-CartItems",
        );
        const cartItemsTarget = document.getElementById("CartDrawer-CartItems");
        if (cartItemsTarget && cartItemsHtml) {
          cartItemsTarget.innerHTML = cartItemsHtml;
        }

        // Update cart footer
        const footerHtml = this.getSectionInnerHTML(
          addResult.sections["cart-drawer"],
          ".cart-drawer__footer",
        );
        const footerTarget = document.querySelector(".cart-drawer__footer");
        if (footerTarget && footerHtml) {
          footerTarget.innerHTML = footerHtml;
        }

        // Update cart icon bubble
        const cartIconHtml = this.getSectionInnerHTML(
          addResult.sections["cart-icon-bubble"],
          ".shopify-section",
        );
        const cartIconTarget = document.getElementById("cart-icon-bubble");
        if (cartIconTarget && cartIconHtml) {
          cartIconTarget.innerHTML = cartIconHtml;
        }
      }

      // Re-enable the cart items
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove("cart__items--disabled");
      }

      // Publish cart update event
      if (
        typeof publish !== "undefined" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-size-selector" });
      }
    } catch (error) {
      console.error("Error changing size:", error);
      // Re-enable the cart items on error
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove("cart__items--disabled");
      }
    } finally {
      this.hideLoadingForItem(lineIndex);
    }
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : null;
  }
}

// Initialize cart size selector 221
document.addEventListener("DOMContentLoaded", () => {
  new CartSizeSelector();
});
